import { db, FirebaseUser } from '../../../firebase';
import {
  collection,
  addDoc,
  doc,
  getDocs,
  updateDoc,
  query,
  orderBy,
  where,
  QueryConstraint
} from 'firebase/firestore';

import { TENANTS_COLLECTION_NAME } from './constants';
import { trackEvent } from '../server/analytics/trackEvent';
import { EventPropertiesMap } from '../server/analytics/events';
import { PlayerReport } from '../../types/report';
import { UserConfig } from '../../types/user';
import { PlayerId } from '../../types/player';
import { StringToAnyMap } from '../../types/common';


// get all reports for all players that the user has access to
export const getAllReports = async (
  userConfig: UserConfig,
  currentUser: FirebaseUser,
  earliestReportDate?: string,
): Promise<PlayerReport[]> => {

  if (userConfig?.accessConfig.reportAccess === 'none') return [];

  const reportsCollectionRef = collection(db, TENANTS_COLLECTION_NAME, userConfig.club, 'reports');

  const queryConstraints: QueryConstraint[] = [orderBy('date', 'desc')];

  if (userConfig.accessConfig.reportAccess === 'personal') {
    queryConstraints.push(where('userEmail', '==', userConfig.email));
  }
  else if (userConfig.accessConfig.reportAccess === 'academy') {
    queryConstraints.push(where('isAcademyReport', '==', true));
  }

  if (earliestReportDate) {
    queryConstraints.push(where('date', '>=', earliestReportDate));
  }

  try {
    const q = query(reportsCollectionRef, ...queryConstraints);
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }) as PlayerReport);
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'getAllReports', errorDetails: { error } }, currentUser, 'system');
    return [];
  }
};


// get all reports for a player that the user has access to
export const getReportsForPlayer = async (
  playerId: PlayerId,
  userConfig: UserConfig,
  currentUser: FirebaseUser,
  shouldBypassAccessCheck?: boolean,
): Promise<PlayerReport[]> => {

  if (userConfig?.accessConfig.reportAccess === 'none' && !shouldBypassAccessCheck) return [];

  const reportsRef = collection(db, TENANTS_COLLECTION_NAME, userConfig.club, 'reports');

  const queryConstraints: QueryConstraint[] = [orderBy('date', 'desc'), where('playerId', '==', playerId)];

  if (userConfig.accessConfig.reportAccess === 'personal' && !shouldBypassAccessCheck) {
    queryConstraints.push(where('userEmail', '==', userConfig.email));
  }
  else if (userConfig.accessConfig.reportAccess === 'academy' && !shouldBypassAccessCheck) {
    queryConstraints.push(where('isAcademyReport', '==', true));
  }

  try {
    const q = query(reportsRef, ...queryConstraints);
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }) as PlayerReport);
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'getReportsForPlayer', errorDetails: { error } }, currentUser, 'system');
    return [];
  }
};


// add a new report and return the document id as the new report id
export const addReport = async (
  report: PlayerReport,
  tenant: string,
  currentUser: FirebaseUser,
): Promise<string | undefined> => {

  const reportsCollectionRef = collection(db, TENANTS_COLLECTION_NAME, tenant, 'reports');

  try {
    const docRef = await addDoc(reportsCollectionRef, report);

    trackEvent('ReportAdded', { reportType: report.reportType }, currentUser, 'user');

    return docRef.id;
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'addReport', errorDetails: { error } }, currentUser, 'system');
    return undefined;
  }
};


// update a report
export const updateReport = async (
  reportId: string,
  newValues: StringToAnyMap,
  tenant: string,
  currentUser: FirebaseUser,
  eventProperties?: EventPropertiesMap['ReportAction'],
): Promise<boolean> => {

  const reportDocRef = doc(db, TENANTS_COLLECTION_NAME, tenant, 'reports', reportId);

  try {
    await updateDoc(reportDocRef, newValues);

    if (eventProperties) trackEvent('ReportAction', eventProperties, currentUser, 'user');

    return true;
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'updateReport', errorDetails: { error } }, currentUser, 'system');
    return false;
  }
};
